import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { ApiError } from "@/api/client";
import {
  createTestTicket,
  deleteTestTicket,
  listTestTickets,
  type TestTicketListItem,
} from "@/api/test-tickets";
import type { FilterStatus } from "@/api/admin";
import { EmptyState } from "@/components/EmptyState";
import { Pagination } from "@/components/Pagination";
import { Spinner } from "@/components/Spinner";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/auth/AuthProvider";
import { formatDateOnly } from "@/lib/formatDate";
import { AdminHeader } from "@/pages/admin/components/AdminHeader";
import { FilterBar } from "@/pages/admin/components/FilterBar";
import { useCurrentTabId, useTabs } from "@/pages/admin/TabsContext";

const PAGE_SIZE = 50;
const VALID_FILTERS: FilterStatus[] = ["Pending", "Acknowledged", "Attachments"];

function parseStatus(sp: URLSearchParams): FilterStatus[] {
  return sp
    .getAll("status")
    .filter((s): s is FilterStatus => (VALID_FILTERS as string[]).includes(s));
}

function parsePage(sp: URLSearchParams): number {
  const n = parseInt(sp.get("page") ?? "1", 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

export function TestTicketsListPage() {
  const [sp, setSp] = useSearchParams();
  const status = parseStatus(sp);
  const q = sp.get("q") ?? "";
  const page = parsePage(sp);
  const offset = (page - 1) * PAGE_SIZE;

  const nav = useNavigate();
  const qc = useQueryClient();
  const tabId = useCurrentTabId();
  const { currentTab } = useTabs();
  const { me } = useAuth();
  const isSuper = me?.role === "super_admin";

  const [showCreate, setShowCreate] = useState(false);

  const query = useQuery({
    queryKey: ["test-tickets", tabId, { status, q, page }],
    queryFn: () => listTestTickets(tabId, { status, q, limit: PAGE_SIZE, offset }),
    placeholderData: keepPreviousData,
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteTestTicket(tabId, id),
    onSuccess: () => {
      toast.success("Test ticket deleted.");
      void qc.invalidateQueries({ queryKey: ["test-tickets", tabId] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Delete failed."),
  });

  const items: TestTicketListItem[] = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = offset + items.length;

  function applyFilters(next: { status: FilterStatus[]; q: string }) {
    const u = new URLSearchParams();
    next.status.forEach((s) => u.append("status", s));
    if (next.q) u.set("q", next.q);
    setSp(u, { replace: true });
  }

  function goToPage(next: number) {
    const u = new URLSearchParams(sp);
    if (next <= 1) u.delete("page");
    else u.set("page", String(next));
    setSp(u, { replace: false });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <div className="min-h-screen flex flex-col">
      <AdminHeader title={`Test tickets · ${currentTab?.name ?? ""}`} backTo="/admin/tabs" />
      <main className="w-full px-4 sm:px-6 lg:px-10 xl:px-14 py-6 flex-1 flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold">Test tickets</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Synthetic tickets stored in Postgres. Never touch the govt DB or the tab's real Qdrant KB.
            </p>
          </div>
          {isSuper && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="btn-primary text-sm px-3 py-1.5 inline-flex items-center gap-1.5"
            >
              <Plus size={14} /> New test ticket
            </button>
          )}
        </div>

        <FilterBar
          status={status}
          q={q}
          onChange={applyFilters}
          onRefresh={() => qc.invalidateQueries({ queryKey: ["test-tickets", tabId] })}
          isRefetching={query.isFetching}
        />

        {query.isLoading ? (
          <div className="card p-12 flex justify-center">
            <Spinner label="Loading test tickets…" />
          </div>
        ) : query.isError ? (
          <div className="alert-danger">
            {(query.error as Error)?.message ?? "Failed to load test tickets."}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title="No test tickets on this tab yet."
            description={
              isSuper
                ? "Click 'New test ticket' to seed one and verify the full workflow."
                : "A superadmin can seed test tickets to help you verify the workflow."
            }
          />
        ) : (
          <div className="card flex flex-col">
            <div className="flex items-center justify-end px-6 py-2 text-xs text-slate-500 dark:text-slate-400 border-b border-canvas-border dark:border-surface-border">
              Showing{" "}
              <span className="mx-1 font-semibold text-slate-700 dark:text-slate-200">
                {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()}
              </span>{" "}
              of{" "}
              <span className="ml-1 font-semibold text-slate-700 dark:text-slate-200">
                {total.toLocaleString()}
              </span>
            </div>
            <div
              className="
                grid grid-cols-[10rem_minmax(0,1fr)_8rem_9rem_10rem_6rem]
                lg:grid-cols-[12rem_minmax(0,1fr)_9rem_11rem_12rem_7rem]
                gap-x-8 lg:gap-x-12 px-6 lg:px-8 py-3
                text-[11px] font-semibold uppercase tracking-wider
                text-slate-500 dark:text-slate-400
                border-b border-canvas-border dark:border-surface-border
              "
            >
              <span>Application ID</span>
              <span>Ticket</span>
              <span>Status</span>
              <span>Submitted</span>
              <span>Attachment</span>
              <span>Action</span>
            </div>

            <ul role="list" className="divide-y divide-canvas-border dark:divide-surface-border/70">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="
                    grid grid-cols-[10rem_minmax(0,1fr)_8rem_9rem_10rem_6rem]
                    lg:grid-cols-[12rem_minmax(0,1fr)_9rem_11rem_12rem_7rem]
                    gap-x-8 lg:gap-x-12 px-6 lg:px-8 py-4 items-center
                    hover:bg-brand-green/25 dark:hover:bg-brand-green/15
                    cursor-pointer transition-colors
                  "
                  role="button"
                  tabIndex={0}
                  onClick={() => nav(`/admin/tabs/${tabId}/test-tickets/${item.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      nav(`/admin/tabs/${tabId}/test-tickets/${item.id}`);
                    }
                  }}
                >
                  <span className="font-mono text-sm font-semibold text-brand-appId truncate">
                    {item.application_id}
                  </span>
                  <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                    {item.grievance_preview || <em className="text-slate-400">–</em>}
                  </span>
                  <span>
                    <StatusBadge status={item.status} />
                  </span>
                  <span className="text-xs font-mono text-slate-500 dark:text-slate-400">
                    {formatDateOnly(item.sent_datetime)}
                  </span>
                  <span className="text-xs text-slate-600 dark:text-slate-300">
                    {item.has_attachment ? "Yes" : "–"}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="btn-secondary text-xs px-2.5 py-1">
                      View <ChevronRight size={12} />
                    </span>
                    {isSuper && (
                      <button
                        type="button"
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 p-1"
                        title="Delete test ticket"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            window.confirm(
                              `Delete test ticket #${item.id}? Its audit rows will also be removed.`,
                            )
                          ) {
                            remove.mutate(item.id);
                          }
                        }}
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            <div className="sticky bottom-0 z-10 border-t border-canvas-border dark:border-surface-border bg-canvas-raised dark:bg-surface-raised rounded-b-lg">
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={goToPage}
                disabled={query.isFetching}
              />
            </div>
          </div>
        )}
      </main>

      {showCreate && (
        <CreateTestTicketModal
          tabId={tabId}
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            setShowCreate(false);
            void qc.invalidateQueries({ queryKey: ["test-tickets", tabId] });
            nav(`/admin/tabs/${tabId}/test-tickets/${id}`);
          }}
        />
      )}
    </div>
  );
}

function CreateTestTicketModal({
  tabId,
  onClose,
  onCreated,
}: {
  tabId: string;
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const [appId, setAppId] = useState("");
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const create = useMutation({
    mutationFn: () =>
      createTestTicket(tabId, {
        application_id: appId,
        grievance_text: text,
        attachment: file,
      }),
    onSuccess: (r) => {
      toast.success(`Test ticket #${r.id} created.`);
      onCreated(r.id);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Create failed."),
  });

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-lg bg-white dark:bg-surface-raised rounded-lg shadow-2xl border border-slate-200 dark:border-surface-border">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-surface-border">
            <DialogTitle className="text-sm font-semibold">Create test ticket</DialogTitle>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-surface-border"
            >
              <X size={16} />
            </button>
          </div>
          <form
            className="p-5 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-slate-600 dark:text-slate-300">Application ID</span>
              <input
                className="input-field font-mono"
                value={appId}
                onChange={(e) => setAppId(e.target.value)}
                required
                maxLength={15}
                placeholder="Type a real ApplicationID to load their history, or a fake one"
              />
              <span className="text-xs text-slate-500">
                If you type a real one, the History Disclosure on the detail page will pull the
                student's real past tickets from the govt DB.
              </span>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-slate-600 dark:text-slate-300">Grievance text</span>
              <textarea
                className="input-field min-h-[120px]"
                value={text}
                onChange={(e) => setText(e.target.value)}
                required
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-slate-600 dark:text-slate-300">
                Attachment (optional): pdf, docx, png, jpg
              </span>
              <input
                type="file"
                accept=".pdf,.docx,.png,.jpg,.jpeg"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </label>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={create.isPending}>
                {create.isPending ? <Spinner label="Creating…" /> : "Create test ticket"}
              </button>
            </div>
          </form>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
